<template>
  <div
    class="sidebar"
    :class="{show: isSideBarVisible || isHomeSideBarShowing, disabled: isDisabled, eu: isEURegion}"
  >
    <div class="flex-grow">
      <div
        class="sidenav-profile"
      >
        <aha-avatar
          :name="`${$store.state.currentUser.firstName} ${$store.state.currentUser.lastName}`"
          :image="$store.state.currentUser.avatar"
          :id="$store.state.currentUser.id"
          :width="40"
          :height="40"
          class="mr-3 text-lg"
        />
        <div>
          <p class="font-bold truncate max-w-32">
            {{ currentUser.firstName }} {{ currentUser.lastName }}
          </p>
          <p class="text-xs truncate sm:max-w-32 max-w-28">
            {{ currentUser.email }}
          </p>
        </div>
      </div>
      <div>
        <ul v-if="!hasOnboardingV3">
          <div class="border-b border-secondary-60"></div>
          <div
            v-if="workingMode === 'personal' && !isEnableAddinPowerpoint"
            class="m-1"
          >
            <router-link
              to="/apps/home"
              tag="li"
              class="sidebar__item"
              active-class="sidebar__item--active"
              @click.native="clickHome"
            >
              <aha-icon name="house" />
              <span class="sidebar__item-name">{{ $i18nPresenter.t('Home') }}</span>
            </router-link>
            <router-link
              to="/apps/presentations"
              tag="li"
              class="sidebar__item"
              active-class="sidebar__item--active"
              @click.native="hideSide"
            >
              <aha-icon name="presentation-chart" />
              <span class="sidebar__item-name">{{ $i18nPresenter.t('My Presentations') }}</span>
            </router-link>
            <router-link
              to="/apps/template-library?from=leftSidebar"
              tag="li"
              :class="['sidebar__item', {'sidebar__item--active': $route.name === 'TemplateLibrary'}]"
              @click.native="hideSide"
            >
              <aha-icon name="magic-wand" />
              <span class="sidebar__item-name">{{ $i18nPresenter.t('Templates') }}</span>
            </router-link>
            <router-link
              to="/apps/shared-presentations"
              tag="li"
              class="sidebar__item"
              active-class="sidebar__item--active"
              @click.native="hideSide"
              v-if="tabShareWithMeVisible"
            >
              <aha-icon name="folder-user" />
              <span class="sidebar__item-name">{{ $i18nPresenter.t('Shared with Me') }}</span>
            </router-link>
            <router-link
              to="/apps/user-plan"
              tag="div"
              class="sidebar__item"
              active-class="sidebar__item--active"
              v-if="currentUser.identifyProvider !== 'skoletube' && !isEnableAddinPowerpoint && !isZoomReady"
              @click.native="hideSide"
            >
              <aha-icon name="plan" />
              <span class="sidebar__item-name">{{ $i18nPresenter.t('My Plan') }}</span>
            </router-link>
            <router-link
              to="/apps/integrations"
              tag="div"
              class="sidebar__item integration-item"
              active-class="sidebar__item--active"
              v-if="currentUser.identifyProvider !== 'skoletube' && !isPresenterWhiteLabel && !isZoomReady"
              @click.native="clickIntegrationsCenter"
            >
              <aha-icon name="squares-four" />
              <span class="sidebar__item-name">{{ $i18nPresenter.t('Integrations') }}</span>
              <section class="stack-icons">
                <aha-icon
                  class="icon"
                  name="zoom"
                  maintain-color
                  viewBox="2 2 24 24"
                  width="21"
                  height="20"
                />
                <aha-icon
                  class="icon"
                  name="Teams"
                  maintain-color
                  viewBox="2 2 24 24"
                  width="20"
                  height="20"
                />
                <aha-icon
                  class="icon"
                  name="google-slides-round"
                  maintain-color
                  viewBox="0 0 16 16"
                  width="20"
                  height="20"
                />
                <aha-icon
                  class="icon"
                  name="PowerPoint"
                  maintain-color
                  viewBox="2 2 24 24"
                  width="20"
                  height="20"
                />
              </section>
            </router-link>
            <div class="border-b border-secondary-60 mt-2 mb-4 -mx-1" />
            <router-link
              to=""
              tag="li"
              class="sidebar__item"
              v-if="isTeamVisible
                && isCasualUserNotHaveTeam"
              @click.native="clickCreateNewTeam"
            >
              <aha-icon name="plus" />
              <span
                class="sidebar__item-name"
              >{{ $i18nPresenter.t('Add team member') }}</span>
            </router-link>
            <router-link
              to="/apps/team/members"
              tag="li"
              class="sidebar__item has-team team-managerment"
              active-class="sidebar__item--active"
              @click.native="clickTeamMembers"
              v-if="isTeamVisible
                && !isCasualUserNotHaveTeam"
            >
              <div
                class="sidebar__item-name grid"
              >
                <span class="truncate overflow-hidden team-name mb-1">{{ currentAccount.name }}</span>
                <div class="flex flex-row items-center">
                  <span class="flex text-xs font-normal gap-1 mr-2">
                    <aha-icon name="users-v2" />
                    {{ currentAccount.numberOfMembers }}
                  </span>
                  <!-- Team plan FREE badge, only show for Free plan team -->
                  <span v-if="isShowFreePlanBadge" class="flex items-center">
                    <span class="mr-2 text-2xs">•</span>
                    <span class="free-plan-badge">FREE</span>
                  </span>
                </div>
              </div>
            </router-link>
            <div v-if="isTeamVisible" class="border-b hidden md:block border-secondary-60 mt-4 mb-2 -mx-1" />
            <span class="desktop-display">
              <router-link
                v-if="canUseTrashBin"
                to="/apps/trash-bin"
                tag="li"
                class="sidebar__item mt-2"
                active-class="sidebar__item--active"
              >
                <aha-icon name="trash" />
                <span class="sidebar__item-name">{{ $i18nPresenter.t('Trash') }}</span>
              </router-link>
            </span>
            <router-link
              to="/apps/white-label"
              tag="div"
              class="sidebar__item"
              active-class="sidebar__item--active"
              v-if="
                currentUser.identifyProvider !== 'skoletube'
                  && currentUser.plan == 'white-label'
              "
              @click.native="hideSide"
            >
              <font-awesome-icon icon="tag" fixed-width size="lg" />
              <span class="sidebar__item-name">{{ $i18nPresenter.t('White Label') }}</span>
            </router-link>
          </div>

          <div v-if="workingMode === 'personal' && isEnableAddinPowerpoint" class="m-2">
            <li
              class="sidebar__item"
              :class="{'sidebar__item--active': $route.name === 'Presentations' || $route.name === 'FolderDetail'}"
              @click="goToPage('/apps/presentations')"
            >
              <aha-icon name="presentation-chart" />
              <span class="sidebar__item-name">{{ $i18nPresenter.t('My Presentations') }}</span>
            </li>
            <li
              class="sidebar__item"
              :class="{'sidebar__item--active': $route.name === 'SharedWithMe' || $route.name === 'ShareFolderDetail'}"
              v-if="tabShareWithMeVisible"
              @click="goToPage('/apps/shared-presentations')"
            >
              <aha-icon name="folder-user" />
              <span class="sidebar__item-name">{{ $i18nPresenter.t('Shared with Me') }}</span>
            </li>
          </div>
        </ul>
        <ul v-else>
          <router-link
            to="/apps/user-plan"
            tag="div"
            class="sidebar__item"
            active-class="sidebar__item--active"
            v-if="currentUser.identifyProvider !== 'skoletube' && !isEnableAddinPowerpoint && !isZoomReady"
            @click.native="hideSide"
          >
            <aha-icon name="plan" />
            <span class="sidebar__item-name">{{ $i18nPresenter.t('My Plan') }}</span>
          </router-link>
        </ul>
      </div>
      <div class="border-b border-secondary-60 mt-4 md:hidden" />

      <div class="m-1 sidebar-resource" v-if="!isPresenterWhiteLabel && !hasOnboardingV3 && !isZoomReady">
        <h5 v-b-toggle.collapse-resources class="sidebar__title has-collapse">
          <span
            class="text"
            :class="{
              'has-new': $store.state.hasNews
            }"
          >{{ $i18nPresenter.t('Resources') }}</span>

          <aha-icon
            class="icon-caret"
            name="arrow"
            width="14"
            height="14"
            viewBox="0 0 24 24"
          />
        </h5>
        <b-collapse :visible="isShowBCollapse" id="collapse-resources">
          <li>
            <a
              href="https://www.youtube.com/channel/UC6ZZtv3C9bveCE8E8tBPhPA"
              class="sidebar__item"
              target="_blank"
              @click="clickVideo"
            >
              <aha-icon name="video" />
              <span class="flex-grow sidebar__item-name">{{ $i18nPresenter.t('Videos') }}</span>
              <aha-icon name="arrow-square-out-v2" class="text-base-80 new-window-icon" />
            </a>
          </li>
          <li>
            <a
              href="https://help.ahaslides.com/portal/en/community/ahaslides/filter/idea/"
              class="sidebar__item"
              target="_blank"
              @click="clickFeatureRequest"
            >
              <aha-icon name="lightbulb-v2" />
              <span class="flex-grow sidebar__item-name">{{ $i18nPresenter.t('Request a Feature') }}</span>
            </a>
          </li>
          <li>
            <a
              href="https://help.ahaslides.com/"
              class="sidebar__item"
              target="_blank"
              @click="clickHelp"
            >
              <aha-icon name="question-v2" />
              <span class="flex-grow sidebar__item-name">{{ $i18nPresenter.t('Help Centre') }}</span>
              <aha-icon name="arrow-square-out-v2" class="text-base-80 new-window-icon" />
            </a>
          </li>
        </b-collapse>
      </div>
      <div class="mobile-display border-b border-secondary-60 md:hidden" />
      <div class="mobile-display">
        <router-link
          v-if="canUseTrashBin"
          to="/apps/trash-bin"
          tag="li"
          class="sidebar__item m-1"
          active-class="sidebar__item--active"
        >
          <i class="fa fa-trash-alt" aria-hidden="true"></i>
          <span class="sidebar__item-name">{{ $i18nPresenter.t('Trash') }}</span>
        </router-link>
      </div>
      <div class="border-b border-secondary-60 md:hidden" v-if="!isEnableAddinPowerpoint && !isZoomReady" />

      <!-- Only visible on mobile -->
      <div class="m-1 mt-3 mb-2 lg:hidden">
        <h5 class="sidebar__title" v-if="!isEnableAddinPowerpoint && !isZoomReady">
          {{ $i18nPresenter.t('My account') }}
        </h5>

        <router-link
          to="/apps/user-profile"
          tag="div"
          class="sidebar__item"
          active-class="sidebar__item--active"
          v-if="currentUser.identifyProvider !== 'skoletube' && !isEnableAddinPowerpoint && !isZoomReady"
          @click.native="hideSide"
        >
          <aha-icon name="aha-user-circle2" />
          <span class="sidebar__item-name">{{ $i18nPresenter.t('My Profile') }}</span>
        </router-link>
        <div
          class="sidebar__item"
          v-if="
            currentUser.identifyProvider !== 'skoletube' && !hasOnboardingV3 && !isEnableAddinPowerpoint && !isZoomReady
          "
          @click="redirectToCustomerPortal"
        >
          <aha-icon name="payment-v2" />
          <span class="sidebar__item-name">{{ $i18nPresenter.t("Billing & Payments") }}</span>
        </div>
        <div class="border-b border-secondary-60 my-2" v-if="!isZoomReady" />
        <div
          class="sidebar__item"
          @click="goNotification()"
        >
          <aha-icon name="aha-bell" />
          <span class="sidebar__item-name">{{ $i18nPresenter.t("Notifications") }}</span>
        </div>
        <div
          v-if="isShowAudienceLoginLink && !isZoomReady"
          class="sidebar__item"
          @click="loginToAudience"
        >
          <aha-icon name="arrow-square-out-v2" />
          <span class="sidebar__item-name">{{ $i18nPresenter.t("sign_in_as_a_participant") }}</span>
        </div>

        <MultipleLanguage v-if="integrationPlatform === 'GoogleSlide'" @click.native="hideSide()" />

        <li v-if="integrationPlatform === 'GoogleSlide'">
          <a
            :href="linkReportPresentation"
            class="sidebar__item"
            :class="{'google-show': integrationPlatform === 'GoogleSlide'}"
            target="_blank"
          >
            <aha-icon
              name="poll"
              width="16"
              height="16"
              viewBox="0 0 21 21"
            />
            <span class="flex-grow sidebar__item-name">{{ $i18nPresenter.t('Report') }}</span>
            <aha-icon name="arrow-square-out-v2" class="text-base-80 new-window-icon" />
          </a>
        </li>
        <li v-if="integrationPlatform === 'GoogleSlide'">
          <a
            href="https://help.ahaslides.com/"
            class="sidebar__item"
            :class="{'google-show': integrationPlatform === 'GoogleSlide'}"
            target="_blank"
          >
            <aha-icon name="aha-question" />
            <span class="flex-grow sidebar__item-name">{{ $i18nPresenter.t('Tutorial') }}</span>
            <aha-icon name="arrow-square-out-v2" class="text-base-80 new-window-icon" />
          </a>
        </li>

        <div class="sidebar__item" @click="handleLogoutButtonClick">
          <aha-icon name="log-out-v2" />
          <span class="sidebar__item-name">{{ $i18nPresenter.t('Log out') }}</span>
        </div>
      </div>
    </div>

    <StickerCta
      open-in-new-tab
      v-if="currentUser.id && sticker && !isZoomReady"
      :sticker="sticker"
    />

    <div class="py-5 text-xs text-center" v-if="!isPresenterWhiteLabel">
      <a
        href="https://ahaslides.com"
        target="AhaSlides_HomePage"
        class="text-link--white"
      >
        {{ $i18nPresenter.t('Powered by AhaSlides') }}
      </a>
      -
      <a
        href="https://ahaslides.com/terms/"
        target="AhaSlides_Terms"
        class="text-link--white"
      >{{ $i18nPresenter.t('Terms') }}</a>
    </div>
    <modal-create-new-team
      v-if="showCreateTeamModal"
      @hide="hideCreatTeamModal"
    />
    <modal-invite-new-member
      v-if="inviteMemberModalVisible"
      @hide="inviteMemberModalVisible = false"
      @updateInvitations="updateInvitations"
      :modalData="modalInviteMemberData"
    />
  </div>
</template>

<script>
import moment from 'moment'
import * as Sentry from '@sentry/vue'
import { mapState, mapActions, mapGetters } from 'vuex'
import utils from '@/util'
import constants from '@/constant'
import { SIDE_NAV } from '@/constant/eventActions'
import config from '@/config'
// api
import StickerApi from '@/api/sticker'
import UserApi from '@/api/user'

// components
import StickerCta from './StickerCta.vue'
import ModalCreateNewTeam from '@/components-v2/modals/CreateNewTeam.vue'
import ModalInviteNewMember from '@/components-v2/modals/InviteNewMember.vue'
import EventBus from '@/eventBus'
import MultipleLanguage from '@/components-v2/multiple-language/MultipleLanguage.vue'

const { eventCategories } = constants

export default {
  components: {
    StickerCta,
    ModalCreateNewTeam,
    ModalInviteNewMember,
    MultipleLanguage,
  },
  props: {
    isSideBarVisible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isShowingSideBar: false,
      sticker: null,
      showCreateTeamModal: false,
      isShowBCollapse: false,
      inviteMemberModalVisible: false,
      modalInviteMemberData: {},
    }
  },
  computed: {
    ...mapGetters('zoomApp', ['isZoomReady']),
    ...mapGetters([
      'currentUserIsTeamOwnerOrAdmin',
      'isPaymentPage',
    ]),
    ...mapGetters('featureToggles', ['canUseTrashBin']),

    ...mapState({
      currentUser: (state) => state.currentUser,
      currentAccount: (state) => state.currentAccount,
      plan: (state) => state.plan,
      presentations: (state) => state.presentations,
      isPresenterWhiteLabel: (state) => state.isPresenterWhiteLabel,
      hasOnboardingV3: (state) => state.indicators.hasOnboardingV3,
      isHomeSideBarShowing: (state) => state.indicators.isHomeSideBarShowing,
      workingMode: (state) => state.workingMode,
      isEnableAddinPowerpoint: (state) => state.isEnableAddinPowerpoint,
      integrationPlatform: (state) => state.integrationPlatform,
      presentation: (state) => state.presentation,
    }),
    isTeamVisible() {
      return this.tabShareWithMeVisible
        && !this.isEURegion
        && !this.isZoomReady
    },
    isDisabled() {
      return this.isPaymentPage
    },
    tabShareWithMeVisible() {
      return this.currentUser.identifyProvider !== 'skoletube'
    },
    isCasualUserNotHaveTeam() {
      return this.currentUser && !this.currentUser.accountId
    },
    isEURegion() {
      return config.region === constants.euRegion
    },
    isShowAudienceLoginLink() {
      return this.currentUser
        && this.currentUser.identifyProvider != 'skoletube'
        && !this.isPresenterWhiteLabel
    },
    myTeamFooterURL() {
      if (this.currentUserIsTeamOwnerOrAdmin) {
        return 'https://form.typeform.com/to/j2O9GXjI/?campaign=pricing?campaign=my-team-admin'
      }
      return 'https://form.typeform.com/to/j2O9GXjI/?campaign=my-team-newuser'
    },

    isShowFreePlanBadge() {
      return this.currentAccount.id && (!this.currentAccount.plan || this.currentAccount.plan === 'free')
    },
    linkReportPresentation() {
      return `${config.presenterDomain}/apps/report/${this.presentation.id}`
    },
  },
  watch: {
    '$store.state.indicators.hasOnboardingV3': function (newVal) {
      if (newVal === false) {
        setTimeout(() => {
          localStorage.setItem('createTeamTooltipShowed', 'onboarding')
        }, 1000)
      }
    },
  },
  async mounted() {
    try {
      this.sticker = await StickerApi.getSticker()
    } catch (error) {
      Sentry.captureException(error)
    }
    this.openBcollapse()

    EventBus.$on('showInviteMemberModal', () => {
      this.showInviteMemberModal()
      utils.mixpanelTracking(
        eventCategories.MY_TEAM,
        'Team Management > Invite member > Show Invite new member dialogue',
        this.currentUser.accountRole === 'owner' ? 'Owner' : 'Casual user',
      )
    })
  },
  methods: {
    ...mapActions(['setIsOnboardingV3ShowingAction', 'createCustomerPortalAction']),
    handleLogoutButtonClick() {
      utils.logout(this.currentUser)
      utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'Logout')
    },
    async redirectToCustomerPortal() {
      this.createCustomerPortalAction()
    },
    hideSide() {
      if (this.hasOnboardingV3) {
        this.setIsOnboardingV3ShowingAction(false)
      }
      if (window.innerWidth > 1023) return
      this.$store.state.indicators.isHomeSideBarShowing = false
    },
    _handleShowWhatsNew() {
      this.$store.state.hasNews = false
      const keyCheckClick = `${this.currentUser.id}-whats-new-check`
      if (!localStorage.getItem(keyCheckClick)) {
        localStorage.setItem(keyCheckClick, moment().format('YYYY MM DD'))
      }
    },
    clickTeamPresentations() {
      this.hideSide()
      const teamId = (this.$store.state.currentAccount.id) || null
      const teamName = (this.$store.state.currentAccount.name) || null
      utils.mixpanelTracking(eventCategories.PRESENTATION_APP, 'SideNav > Team Presentations', teamName, '', {
        teamId,
      })
    },
    clickTeamMembers() {
      this.hideSide()
      const teamId = (this.$store.state.currentAccount.id) || null
      const teamName = (this.$store.state.currentAccount.name) || null
      utils.mixpanelTracking(eventCategories.PRESENTATION_APP, 'SideNav > Team Members', teamName, '', {
        teamId,
      })
    },
    clickWhatsNew() {
      this.hideSide()
      this._handleShowWhatsNew()
      utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'SideNav > What\'s New')
    },
    clickHelp() {
      this.hideSide()
      utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'SideNav > Help')
    },
    clickFeatureRequest() {
      this.hideSide()
      utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'SideNav > Feature Request')
    },
    clickVideo() {
      this.hideSide()
      utils.mixpanelTracking(eventCategories.PRESENTATION_LIST, 'SideNav > Videos')
    },
    clickCreateNewTeam() {
      utils.mixpanelTracking(utils.getGACategories(this.$route.name), 'SideNav > Create a team', '', '', {
        userId: this.currentUser.id,
        plan: this.currentUser.plan,
      })
      this.showInviteMemberModal()
    },
    clickHome() {
      const eventAction = SIDE_NAV.HOME
      const trackData = {
        eventCategory: this.$route.name,
        eventAction,
      }
      utils.trackGA4AndMixpanel(eventAction, trackData)
      this.hideSide()
    },
    clickIntegrationsCenter() {
      const trackData = {
        eventCategory: this.$route.name,
        eventAction: SIDE_NAV.INTEGRATIONS,
      }
      utils.trackGA4AndMixpanel('Click on integrations in nav menu', trackData)
      this.hideSide()
    },
    hideCreatTeamModal() {
      this.showCreateTeamModal = false
    },
    openBcollapse() {
      setTimeout(() => {
        this.isShowBCollapse = true
      }, 1500)
    },
    goToPage(path) {
      window.location.href = `${config.presenterDomain}${path}`
    },
    loginToAudience() {
      window.open(`${config.audienceDomain}/?tab=account&from=presenter`, '_blank')

      utils.trackGA4AndMixpanel(
        'Sign in as a participant',
        {
          eventCategory: 'Presentation List',
          email: this.currentUser.email,
          userId: this.currentUser.id,
        },
      )
    },
    showInviteMemberModal() {
      this.modalInviteMemberData = {
        plan: this.currentUser.plan,
        numberOfUsedLicenses: this.isCasualUserNotHaveTeam ? 1 : this.currentAccount.numberOfUsedLicenses,
        licenseQuantity: this.isCasualUserNotHaveTeam ? 1 : this.currentAccount.licenseQuantity,
        isCasualUserNotHaveTeam: this.isCasualUserNotHaveTeam,
        myTeamFooterURL: this.myTeamFooterURL,
        planEndAt: this.currentAccount.planEndAt,
      }
      this.inviteMemberModalVisible = true
    },
    updateInvitations() {
      EventBus.$emit('handleUpdateInvitationsAndLicense')
    },
    async goNotification() {
      this.hideSide()
      this.$store.state.isShowNotificationPanel = !this.$store.state.isShowNotificationPanel
      if (this.$store.state.isShowNotificationPanel) {
        this.$store.state.notificationUnread = 0
        await UserApi.patchUserById(this.currentUser.id, {
          notificationReadTime: new Date(),
        })
      }
      this._trackEvent('Side Bar > Noti > Open', {})
    },
    _trackEvent(action, data, event_label = null) {
      utils.mixpanelTracking(
        this.$route.name,
        action,
        event_label,
        null,
        data,
      )
    },
  },
}
</script>

<style lang="scss">
@import "@/assets/scss/animation.scss";
$total-icons: 4;

.integration-item {
  & > .stack-icons {
    --icon-size: 1.25rem;
    --space: 0.85rem;
    --margin-space: 0.5rem;

    display: grid;
    margin-inline-start: var(--margin-space);
    grid-template-areas: "main";
    & > * {
      grid-area: main;
      aspect-ratio: 1 / 1;
      width: var(--icon-size);
      border-radius: 50%;
      overflow: visible;
      transform: translateX(0px);
      transition: all 363ms;
      background-color: #3F4557;
      box-sizing: border-box;
      padding: 2px;
      box-shadow: 0 0 0 1px #282e3f;

      @for $i from 1 through $total-icons {
        &:nth-child(#{$i}) {
          transform: translateX(calc($i * 2px));
        }
      }
    }
  }

  &:hover > .stack-icons {
    & > * {
      transition: all cubic-bezier(0.16, 1.93, 0.33, 0.85) 363ms;

      &:nth-child(#{$total-icons}) {
        transform: translateX(calc($total-icons * 4px));
      }
    }
  }

  &.sidebar__item--active > .stack-icons {
    & > * {
      box-shadow: 0 0 0 1px #191F2D;
      transition: all cubic-bezier(0.16, 1.93, 0.33, 0.85) 363ms;

      @for $i from 2 through $total-icons {
        &:nth-child(#{$i}) {
          transform: translateX(calc(($i - 1) * var(--space)));
        }
      }
    }
  }
}

.sidebar-resource, .mobile-display{
  // only view in mobile
  @media screen and (min-width: 640px) {
    display: none;
  }
}

.desktop-display {
    @media screen and (max-width: 639px) {
      display: none;
    }
}
.bell-ring {
  -webkit-animation: ring 2s .2s ease-in-out infinite;
  -webkit-transform-origin: 50% 4px;
  -moz-animation: ring 2s .2s ease-in-out infinite;
  -moz-transform-origin: 50% 4px;
  animation: ring 2s .2s ease-in-out infinite;
  transform-origin: 50% 4px;
}

.sidenav-profile {
  @apply lg:hidden flex px-4 py-5;
}

.sidebar {
  @apply text-sm flex flex-col text-base-0 z-10 lg:z-0;
  @apply fixed -left-60 top-0 bottom-0 overflow-auto;
  @apply lg:flex lg:top-15;
  background-color: theme('colors.indigo.90');
  transition: .15s ease;
  z-index: 22;
  width: 14rem;
}

.sidebar.disabled {
  @apply hidden;
}
.sidebar.show {
  @apply left-0;
}
.sidebar__item {
  @apply transition-colors text-base-0 text-opacity-60 rounded-lg;
  .top {
    @apply relative -top-2.5;
  }

  & i {
    padding: 0 4px 0 2px;
  }
}

.new-window-icon {
  visibility: hidden;
}

.sidebar__item,
.sidebar__item--active {
  @apply h-10 px-3 flex items-center cursor-pointer;

  svg, i {
    flex-shrink: 0;
  }

  &.referral {
    @apply h-16;
  }

  &:hover {
    .new-window-icon {
      visibility: visible;
    }
  }
}

.sidebar__item:hover:not(.sidebar__item--active) {
  color: theme('colors.white.100');
}

.sidebar__item--active {
  @apply text-base-0;
  background-color: theme('colors.primary.90');

  & svg, i {
    color: theme('colors.white.100');
  }
}

.sidebar__item-name {
  @apply overflow-hidden whitespace-nowrap overflow-ellipsis;
  @apply ml-2 font-bold;
}

.team-managerment {
  .sidebar__item-name {
    margin-left: 0;
  }
}

.sidebar__item {
  &.has-team {
    padding: 12px 16px;
    height: auto;
  }
  &.selected.has-team {
    border: 0;

    svg {
      color: inherit;
    }
  }
}

.sidebar__title {
  &:not(.has-collapse) {
    @apply mb-3 mt-1 lg:mt-6 ml-4 font-medium text-xs mr-1;
    @apply text-base-0 text-opacity-40 uppercase tracking-widest;
  }

  &.has-collapse {
    @apply flex justify-between;
    @apply mt-3 mb-2 ml-4 font-medium text-xs;

    .text,
    .icon-caret {
      @apply text-base-0 text-opacity-40 uppercase tracking-widest relative;
    }

    &.collapsed {
      .text {
        @apply text-opacity-100;

        &.has-new::after {
          content: '';
          position: absolute;
          top: 5px;
          right: -10px;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background-color: #FF4954;
          box-shadow: 0px 4.00126px 4.00126px rgba(255, 73, 84, 0.3);
        }
      }

      .icon-caret {
        transform: rotateX(180deg);
      }
    }
  }
}

.whats-new__icon-wrapper {
  width: 24px;
}
.credit {
  font-weight: normal;
  font-size: 12px;
}

.sidebar li {
  list-style: none;
}

.create-team--effect-tooltip {
  @media screen and (max-width: 640px) {
    display: none
  }
  @apply shadow-lg;
  width: 14rem;
  height: 12.5rem;
  background: #FFFFFF;
  padding: 8px 12px;
  border-radius: 8px;
  position: absolute;
  left: 4.75rem;
  bottom: -7.5rem;
  .title {
    font-weight: 700;
    font-size: 12px;
    line-height: 23px;
    color: #282E3E;
  }
  span {
    color: #282E3E;
  }
  img {
    margin-top: 8px;
  }
  .effect--tooltip__tip {
    position: absolute;
    width: 7px;
    z-index: 1;
    height: 7px;
    bottom: 50%;
    left: 0px;
    background: #FFFFFF;
    transform: translate(-50%, 0) rotate(45deg);
    border-bottom-right-radius: 2px;
    outline: none;
  }
}

.free-plan-badge {
  @apply bg-base-20 text-base-90 rounded-xl px-1.5 py-0.25;
  font-size: 10px;
  height:14px;
  line-height: 14px;
}
</style>
